import { QueryClient } from '@tanstack/react-query';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import { Reminder } from '../reminder/_types';
import { normalizeReminder } from '../reminder/normalizers';

import {
  AutomationGroup,
  AutomationLayout,
  ReorderAutomationLayoutPayload,
} from './_types';

const normalizeAutomationGroup = (group: Record<string, unknown>): AutomationGroup => {
  return camelcaseKeys(group, { deep: true }) as AutomationGroup;
};

export const automationGroupsQueryKey = () => ['automation-groups'] as const;

export const automationsQueryKey = (customType?: string) =>
  ['automations', customType] as const;

const reminderIdsMatch = (
  left: Reminder['id'],
  right: Reminder['id']
): boolean => left != null && right != null && String(left) === String(right);

const sortByPosition = <T extends { position?: number }>(items: T[]) =>
  [...items].sort(
    (left, right) => (left.position ?? 0) - (right.position ?? 0)
  );

/** Places reminders under their automation groups on the client. */
export const buildAutomationLayout = (
  groups: AutomationGroup[],
  reminders: Reminder[]
): AutomationLayout => {
  const sortedGroups = sortByPosition(groups);
  const sortedReminders = sortByPosition(reminders);
  const groupIds = new Set(sortedGroups.map((group) => String(group.id)));

  const isUngrouped = (reminder: Reminder) =>
    reminder.automationGroupId == null ||
    !groupIds.has(String(reminder.automationGroupId));

  const ungroupedReminders = sortedReminders.filter(isUngrouped);

  const automationGroups = sortedGroups.map((group) => ({
    ...group,
    reminders: sortedReminders.filter(
      (reminder) =>
        reminder.automationGroupId != null &&
        String(reminder.automationGroupId) === String(group.id)
    ),
  }));

  return { automationGroups, ungroupedReminders };
};

export const patchReminderInAutomationsCache = (
  queryClient: QueryClient,
  savedReminder: Reminder
) => {
  const normalized = normalizeReminder(savedReminder);

  queryClient.setQueriesData<Reminder[]>(
    { queryKey: ['automations'] },
    (current) => {
      if (!current) {
        return current;
      }

      const hasReminder = current.some((reminder) =>
        reminderIdsMatch(reminder.id, savedReminder.id)
      );

      if (hasReminder) {
        return current.map((reminder) =>
          reminderIdsMatch(reminder.id, savedReminder.id)
            ? { ...reminder, ...normalized }
            : reminder
        );
      }

      return [...current, normalized];
    }
  );
};

export const patchRemindersAfterGroupUpdate = (
  queryClient: QueryClient,
  groupId: Identifier,
  reminderIds: Identifier[]
) => {
  const reminderIdSet = new Set(reminderIds.map((id) => String(id)));
  const normalizedGroupId = Number(groupId);

  queryClient.setQueriesData<Reminder[]>(
    { queryKey: ['automations'] },
    (current) => {
      if (!current) {
        return current;
      }

      return current.map((reminder) => {
        const inGroup = String(reminder.automationGroupId) === String(groupId);
        const shouldBeInGroup = reminderIdSet.has(String(reminder.id));

        if (inGroup && !shouldBeInGroup) {
          return { ...reminder, automationGroupId: null };
        }

        if (shouldBeInGroup) {
          return { ...reminder, automationGroupId: normalizedGroupId };
        }

        return reminder;
      });
    }
  );
};

/** Clears automationGroupId on reminders whose group no longer exists. */
export const patchRemindersWithMissingGroups = (
  queryClient: QueryClient,
  groups: AutomationGroup[]
) => {
  const groupIds = new Set(groups.map((group) => String(group.id)));

  queryClient.setQueriesData<Reminder[]>(
    { queryKey: ['automations'] },
    (current) => {
      if (!current) {
        return current;
      }

      let changed = false;
      const next = current.map((reminder) => {
        if (
          reminder.automationGroupId != null &&
          !groupIds.has(String(reminder.automationGroupId))
        ) {
          changed = true;
          return { ...reminder, automationGroupId: null };
        }

        return reminder;
      });

      return changed ? next : current;
    }
  );
};

export const patchRemindersAfterGroupDelete = (
  queryClient: QueryClient,
  groupId: Identifier
) => {
  queryClient.setQueriesData<AutomationGroup[]>(
    { queryKey: automationGroupsQueryKey() },
    (current) =>
      current?.filter((group) => String(group.id) !== String(groupId))
  );

  queryClient.setQueriesData<Reminder[]>(
    { queryKey: ['automations'] },
    (current) => {
      if (!current) {
        return current;
      }

      return current.map((reminder) =>
        String(reminder.automationGroupId) === String(groupId)
          ? { ...reminder, automationGroupId: null }
          : reminder
      );
    }
  );
};

export const fetchAutomationGroups = async (): Promise<AutomationGroup[]> => {
  const response = await api.get('/api/v4/automation_groups');

  const data = camelcaseKeys(response.data, { deep: true }) as {
    automationGroups?: AutomationGroup[];
  };

  return (data.automationGroups || []).map((group) =>
    normalizeAutomationGroup(group as unknown as Record<string, unknown>)
  );
};

export interface CreateAutomationGroupPayload {
  description?: string;
  name: string;
  reminderIds: Identifier[];
}

export const createAutomationGroup = async (
  payload: CreateAutomationGroupPayload
): Promise<AutomationGroup> => {
  const response = await api.post('/api/v4/automation_groups', {
    automation_group: {
      description: payload.description?.trim() || undefined,
      name: payload.name,
      reminder_ids: payload.reminderIds,
    },
  });

  return normalizeAutomationGroup(response.data);
};

export interface UpdateAutomationGroupPayload {
  description?: string;
  name?: string;
  reminderIds?: Identifier[];
}

export const updateAutomationGroup = async (
  id: Identifier,
  payload: UpdateAutomationGroupPayload
): Promise<AutomationGroup> => {
  const response = await api.put(`/api/v4/automation_groups/${id}`, {
    automation_group: {
      description: payload.description?.trim() || undefined,
      name: payload.name,
      reminder_ids: payload.reminderIds,
    },
  });

  return normalizeAutomationGroup(response.data);
};

export const deleteAutomationGroup = async (id: Identifier): Promise<void> => {
  await api.delete(`/api/v4/automation_groups/${id}`);
};

export const reorderAutomationLayout = async (
  payload: ReorderAutomationLayoutPayload
): Promise<void> => {
  await api.put('/api/v4/automation_groups/reorder', {
    groups: payload.groups.map((group) => ({
      id: group.id,
      position: group.position,
      reminder_ids: group.reminderIds,
    })),
    ungrouped_reminder_ids: payload.ungroupedReminderIds,
  });
};

export const flattenAutomationLayout = (layout: AutomationLayout): Reminder[] => {
  const grouped = layout.automationGroups.flatMap((group) => group.reminders || []);
  return [...layout.ungroupedReminders, ...grouped];
};
